<script>
import {useVuelidate} from '@vuelidate/core';
import {helpers, maxLength, required, requiredIf} from '@vuelidate/validators';

/* COMPOSANTS */
import AkInputText from '@components/v2/input/AkInputText.vue';
import AkDropdownGroup from '@components/v2/input/AkDropdownGroup.vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import AkMultiSelect from '@components/v2/input/AkMultiSelect.vue';
import GaMessages from '@components/ga/layout/GaMessages.vue';
import AkSidebar from '@components/v2/general/AkSidebar.vue';
import AkButtonFooterSidebar from '@components/v2/general/AkButtonFooterSidebar.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';
import companyMixin from '@mixins/companyMixin';
import poleMixin from '@mixins/poleMixin';
import employeeFormMixin from '@mixins/employeeFormMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import formatMixin from '@mixins/formatMixin';
import validatorMixins from '@mixins/validatorMixins';
import civilitiesConst from '@mixins/const/civilitiesConst';

/* SERVICES */
import employeeService from '@services/employeeService';

export default {
  components: {
    AkButtonFooterSidebar,
    AkSidebar,
    GaMessages,
    AkMultiSelect,
    AkDropdown,
    AkDropdownGroup,
    AkInputText,
    AkFormSubmitted,
  },
  mixins: [
    utilsMixin,
    messageMixin,
    companyMixin,
    poleMixin,
    employeeFormMixin,
    formatMixin,
    permissionsMixin,
    validatorMixins,
    civilitiesConst,
  ],
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      createSidebarVisible: false,
      submitted: false,
    };
  },
  validations() {
    return {
      employee: {
        civility: {required},
        firstName: {
          required,
          maxLengthValue: maxLength(100),
          customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
        },
        lastName: {
          required,
          maxLengthValue: maxLength(100),
          customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
        },
        email: {
          required,
          maxLengthValue: maxLength(100),
          email: helpers.withMessage(
            "Le format de l'adresse e-mail est invalide. Veuillez vérifier et réessayer (ex. : nom@domaine.com)",
            this.emailValidator,
          ),
        },
        roleKey: {required},
        roleType: {required},
        companyId: {required: requiredIf(this.isRoleTypeGav)},
      },
    };
  },
  emits: ['creation'],
  methods: {
    show() {
      this.createSidebarVisible = true;
      this.submitted = false;
      if (this.isGas()) {
        this.employee.roleType = 'GAS';
      } else if (this.isGav()) {
        this.employee.roleType = 'GAV';
      }
    },
    hide() {
      this.createSidebarVisible = false;
      this.employee = {};
    },
    create() {
      this.submitted = true;
      this.v$.employee.$touch();
      if (this.v$.employee.$error) {
        this.$refs.footerSidebar.init();
        return;
      }

      employeeService
        .create(this.employee)
        .then(() => {
          this.success(this.$t('employee.added'));
          this.employee = {};
          this.$emit('creation');
          this.hide();
        })
        .catch((e) => {
          const msg = this.msgForErr(this.$t, e);
          this.$refs.sidebarMessages.error(msg);
          this.$refs.footerSidebar.init();
        })
        .finally(() => {
          this.submitted = false;
        });
    },
  },
  computed: {},
};
</script>

<template>
  <AkSidebar
    v-model:visible="createSidebarVisible"
    :baseZIndex="9998"
    :dismissable="true"
    block-scroll
    position="right"
    @hide="hide">
    <template #container>
      <div class="mb-4">
        <GaMessages ref="sidebarMessages" />
      </div>
      <div class="p-sidebar-header">
        <div class="p-sidebar-header-content">{{ $t('employee.add') }}</div>
        <button class="p-sidebar-close p-sidebar-icon p-link" type="button" @click="hide">
          <span
            class="p-sidebar-close-icon ga-icon ga-close_circle text-4xl text-grey-400 md:hover:text-grey-900 md:hover:bg-transparent" />
        </button>
      </div>
      <div class="p-sidebar-content">
        <perfect-scrollbar class="h-full pr-7" :options="{suppressScrollX: true}">
          <AkFormSubmitted :submitted="submitted" class="flex flex-col gap-4 md:gap-2" reference="form">
            <AkDropdown
              v-model="employee.roleType"
              :label="$t('employee.roleType')"
              :options="roleTypesForEmployees"
              :disabled="isGas() || isGav()"
              :validator="v$.employee.roleType" />
            <AkMultiSelect
              v-if="isRoleTypeGas"
              v-model="employee.poleIds"
              :label="$t('team')"
              :options="poles"
              option-label="name"
              option-value="id" />
            <AkDropdown
              v-if="isRoleTypeGav"
              v-model="employee.roleKey"
              :label="$t('employee.roleKey')"
              :options="buildRoleKeys"
              :validator="v$.employee.roleKey" />
            <AkDropdownGroup
              v-if="isRoleTypeGas"
              v-model="employee.roleKey"
              :label="$t('employee.roleKey')"
              :options="buildRoleKeys"
              :validator="v$.employee.roleKey" />
            <AkDropdown
              v-if="isRoleTypeGav"
              v-model="employee.companyId"
              :label="$t('employee.company')"
              :options="companies"
              :validator="v$.employee.companyId"
              option-label="name"
              option-value="id" />
            <AkDropdown
              v-model="employee.civility"
              :label="$t('employee.civility')"
              :options="civilitiesConst"
              :validator="v$.employee.civility" />
            <AkInputText
              v-model="employee.lastName"
              :label="$t('employee.lastName')"
              :formatter="toUppercase"
              :validator="v$.employee.lastName" />
            <AkInputText
              v-model="employee.firstName"
              :label="$t('employee.firstName')"
              :validator="v$.employee.firstName" />
            <AkInputText
              v-model="employee.email"
              :label="$t('employee.email')"
              :validator="v$.employee.email"
              type="email"
              icon="ga-icon ga-email" />

            <AkButtonFooterSidebar
              ref="footerSidebar"
              :validate-label="$t('add')"
              :cancel-label="$t('cancel')"
              @cancel="hide()"
              @action="create()" />
          </AkFormSubmitted>
        </perfect-scrollbar>
      </div>
    </template>
  </AkSidebar>
</template>
